"use client";

import { useState } from "react";
import Link from "next/link";
import { Logo, Arrow } from "./Icon";
import ThemeToggle from "./ThemeToggle";

interface NavItem {
  slug: string;
  title: string;
}

interface ChromeNavItem {
  label: string;
  href: string;
  kind?: "link" | "industries-dropdown" | "solutions-dropdown";
}
interface ChromeNavLink { label: string; href: string }

interface Brand {
  companyName: string;
  tagline: string;
  imageUrl?: string;
  alt: string;
}

export default function Header({
  industries,
  solutions,
  nav,
  ctaButton,
  brand,
}: {
  industries: NavItem[];
  solutions: NavItem[];
  nav: ChromeNavItem[];
  ctaButton: ChromeNavLink;
  brand: Brand;
}) {
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState<string | null>(null);

  /**
   * Resolve a nav item to its rendering kind. Prefer the explicit `kind` field;
   * fall back to label-substring matching for back-compat with older content.
   */
  function dropdownKind(item: ChromeNavItem): "industries" | "solutions" | null {
    if (item.kind === "industries-dropdown") return "industries";
    if (item.kind === "solutions-dropdown") return "solutions";
    if (item.kind === "link") return null;
    const lower = item.label.toLowerCase();
    if (lower.includes("industr")) return "industries";
    if (lower.includes("solution")) return "solutions";
    return null;
  }

  return (
    <header className="sticky top-0 z-50 bg-white shadow-xs dark:bg-navy-dark dark:shadow-black/40">
      <div className="container-x flex h-24 items-center justify-between gap-6 sm:h-28">
        <Link href="/" aria-label={brand.alt} className="shrink-0">
          <Logo
            companyName={brand.companyName}
            tagline={brand.tagline}
            imageUrl={brand.imageUrl}
            alt={brand.alt}
          />
        </Link>

        <nav className="hidden items-center gap-5 whitespace-nowrap xl:mr-8 xl:flex">
          {nav.map((item) => {
            const kind = dropdownKind(item);
            if (kind === "industries") {
              return (
                <Dropdown key={item.href} label={item.label} href={item.href} items={industries} base="/industries" />
              );
            }
            if (kind === "solutions") {
              return (
                <Dropdown key={item.href} label={item.label} href={item.href} items={solutions} base="/solutions" />
              );
            }
            return (
              <NavLink key={item.href} href={item.href}>{item.label}</NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle className="hidden sm:inline-flex" />
          <Link href={ctaButton.href} className="hidden shrink-0 whitespace-nowrap btn-red px-5! py-2.5! sm:inline-flex">
            {ctaButton.label}
          </Link>
          <button
            className="xl:hidden p-2 text-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-gray-100 bg-white xl:hidden dark:border-white/10 dark:bg-navy-dark">
          <div className="container-x flex flex-col py-3">
            {nav.map((item) => {
              const kind = dropdownKind(item);
              if (kind === "industries") {
                return (
                  <MobileGroup
                    key={item.href}
                    label={item.label}
                    href={item.href}
                    items={industries}
                    base="/industries"
                    expanded={mobileSub === "ind"}
                    onToggle={() => setMobileSub(mobileSub === "ind" ? null : "ind")}
                    onNavigate={() => setOpen(false)}
                  />
                );
              }
              if (kind === "solutions") {
                return (
                  <MobileGroup
                    key={item.href}
                    label={item.label}
                    href={item.href}
                    items={solutions}
                    base="/solutions"
                    expanded={mobileSub === "sol"}
                    onToggle={() => setMobileSub(mobileSub === "sol" ? null : "sol")}
                    onNavigate={() => setOpen(false)}
                  />
                );
              }
              return (
                <MobileLink key={item.href} href={item.href} onClick={() => setOpen(false)}>{item.label}</MobileLink>
              );
            })}
            <Link href={ctaButton.href} className="btn-red mt-3" onClick={() => setOpen(false)}>
              {ctaButton.label}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="whitespace-nowrap text-sm font-semibold uppercase tracking-normal text-navy/90 hover:text-brand-red dark:text-gray-200 dark:hover:text-brand-red"
    >
      {children}
    </Link>
  );
}

function Dropdown({
  label,
  href,
  items,
  base,
}: {
  label: string;
  href: string;
  items: NavItem[];
  base: string;
}) {
  return (
    <div className="group relative">
      <Link
        href={href}
        className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold uppercase tracking-normal text-navy/90 hover:text-brand-red dark:text-gray-200 dark:hover:text-brand-red"
      >
        {label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </Link>
      <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
        <ul className="overflow-hidden rounded-md border border-gray-100 bg-white py-2 shadow-xl dark:border-white/10 dark:bg-navy">
          {items.map((it) => (
            <li key={it.slug}>
              <Link
                href={base ? `${base}/${it.slug}` : `/${it.slug}`}
                className="block px-4 py-2 text-sm text-navy/80 hover:bg-gray-50 hover:text-brand-red dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-brand-red"
              >
                {it.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MobileLink({ href, children, onClick }: { href: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="border-b border-gray-100 py-3 text-sm font-semibold uppercase tracking-wide text-navy dark:border-white/10 dark:text-gray-100"
    >
      {children}
    </Link>
  );
}

function MobileGroup({
  label, href, items, base, expanded, onToggle, onNavigate,
}: {
  label: string; href: string; items: NavItem[]; base: string;
  expanded: boolean; onToggle: () => void; onNavigate: () => void;
}) {
  return (
    <div className="border-b border-gray-100 dark:border-white/10">
      <div className="flex items-center justify-between py-3">
        <Link href={href} onClick={onNavigate} className="text-sm font-semibold uppercase tracking-wide text-navy dark:text-gray-100">
          {label}
        </Link>
        <button onClick={onToggle} className="p-1 text-navy dark:text-gray-100" aria-label="Expand">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            className={expanded ? "rotate-180" : ""}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>
      {expanded && (
        <ul className="pb-2">
          {items.map((it) => (
            <li key={it.slug}>
              <Link
                href={base ? `${base}/${it.slug}` : `/${it.slug}`}
                onClick={onNavigate}
                className="block py-2 pl-3 text-sm text-navy/70 dark:text-gray-300"
              >
                {it.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}